/**
 * The flagship celebration on web: a one-shot rainbow sweep across the
 * composer interior when the user picks a Pro plan model (`glimm`, WebGL).
 *
 * The canvas sits inside the composer shell, invisible at rest. The shader
 * library is loaded on the first sweep only (a dynamic import from a `.web`
 * file), so it never reaches the native bundle or the first paint.
 */
import { useEffect, useRef } from 'react';
import { subscribeComposerCelebration } from '@/lib/composer-celebration';

type Glimm = typeof import('glimm');

let glimmModule: Promise<Glimm> | null = null;

function loadGlimm() {
  glimmModule ??= import('glimm');
  return glimmModule;
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function ComposerSweepCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const shaderRef = useRef<ReturnType<Glimm['createShader']> | null>(null);
  const sweepingRef = useRef(false);

  useEffect(() => {
    let alive = true;

    const play = async () => {
      if (sweepingRef.current || prefersReducedMotion()) return;
      const canvas = canvasRef.current;
      // A hidden composer (a collapsed column) has nothing to sweep across.
      if (!canvas || canvas.clientWidth === 0) return;
      sweepingRef.current = true;
      try {
        const glimm = await loadGlimm();
        if (!alive || !canvasRef.current) return;
        const rainbow = glimm.accentChain([
          glimm.ACCENTS.red,
          glimm.ACCENTS.orange,
          glimm.ACCENTS.yellow,
          glimm.ACCENTS.green,
          glimm.ACCENTS.cyan,
          glimm.ACCENTS.blue,
          glimm.ACCENTS.purple,
        ]);
        const direction =
          typeof document !== 'undefined' && document.dir === 'rtl' ? 'rtl' : 'ltr';

        // Recreated for every sweep so the shader clock starts at 0, and
        // Math.random pinned while it is built: glimm seeds its hue phase from
        // it, which would otherwise tint the rainbow differently every time.
        shaderRef.current?.destroy();
        const random = Math.random;
        Math.random = () => 0;
        let shader: ReturnType<Glimm['createShader']>;
        try {
          shader = glimm.createShader({
            canvas: canvasRef.current,
            palette: rainbow,
            direction,
            bandTight: 10,
            swellAmount: 0.85,
          });
        } finally {
          Math.random = random;
        }
        shaderRef.current = shader;
        if (!shader) return;

        const sweep = glimm.playSweep(shader, {
          palette: rainbow,
          direction,
          sweepMs: 570,
          outroMs: 80,
          peakAlpha: 1.3,
          bandTight: 10,
          brightness: 1.4,
          swellAmount: 1,
          waveSpeed: 1.8,
          easing: 'easeOutExpo',
        });
        await sweep.done;
      } catch {
        // No WebGL, or the chunk failed to load: the pick itself already
        // happened, the celebration is decoration.
      } finally {
        sweepingRef.current = false;
      }
    };

    const unsubscribe = subscribeComposerCelebration(() => {
      void play();
    });
    return () => {
      alive = false;
      unsubscribe();
      shaderRef.current?.destroy();
      shaderRef.current = null;
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden className="tabbio-composer-sweep" />;
}
